<template>
  <transition name="edit-fade" mode="out-in">
    <div class="columns blocks-wrapper" v-if="!edit && !add" :key="'1'">
      <loader :loading="loading.blocks" :fixed="false"></loader>
      <div class="column is-8">
        <div class="blocks-header">
          <span>{{ $resx("Blocks.Active") }}</span
          ><span>{{ $resx("Blocks.All") }}</span>
        </div>
        <draggable
          class="blocks-list onpage"
          :tag="'div'"
          @start="drag = true"
          @end="drag = false"
          group="blocks"
          v-model="blocks"
          v-bind="dragOptions"
        >
          <transition-group
            tag="ul"
            type="transition"
            :name="!drag ? 'flip-list' : null"
          >
            <li
              v-for="(block, idx) in blocks"
              :key="idx + 1"
              @click="selectContent(block)"
              :class="{ selected: selected === block.ID }"
            >
              <p>{{ block.ID }}. {{ block.Title }}</p>
              <small class="is-size-7">{{ block.Subtitle | striphtml }}</small>
            </li>
          </transition-group>
        </draggable>
        <draggable
          class="blocks-list"
          :tag="'div'"
          @start="drag = true"
          @end="drag = false"
          group="blocks"
          :sort="false"
          v-model="contentBlocks"
          v-bind="dragOptions"
        >
          <transition-group
            tag="ul"
            type="transition"
            :name="!drag ? 'flip-list' : null"
          >
            <li
              v-for="(block, idx) in contentBlocks"
              :key="idx + 1"
              @click="selectContent(block)"
              :class="{ selected: selected === block.ID }"
            >
              <p>{{ block.ID }}. {{ block.Title }}</p>
              <small class="is-size-7">{{ block.Subtitle | striphtml }}</small>
            </li>
          </transition-group>
        </draggable>
      </div>
      <div class="column is-4">
        <div class="buttons">
          <button class="button is-fullwidth" @click.prevent="addBlock">
            <span class="icon">
              <font-awesome-icon :icon="icons.add" />
            </span>
            <span>{{ $resx("Buttons.Add") }}</span>
          </button>
          <button
            class="button is-fullwidth"
            :disabled="selected > -1 ? false : 'disabled'"
            @click.prevent="
              (edit = true),
                $emit('canClose', false),
                $store.commit('contentChanged', true)
            "
          >
            <span class="icon">
              <font-awesome-icon :icon="icons.edit" />
            </span>
            <span>{{ $resx("Buttons.Edit") }}</span>
          </button>
          <button
            class="button is-fullwidth is-danger"
            :disabled="selected > -1 ? false : 'disabled'"
            @click.prevent="deleteBlock"
          >
            <span class="icon">
              <font-awesome-icon :icon="icons.delete" />
            </span>
            <span>{{ $resx("Buttons.Delete") }}</span>
          </button>
        </div>
      </div>
    </div>
    <div class="columns form-wrapper" v-else :key="'2'">
      <loader :loading="loading.edit" :fixed="false"></loader>
      <form class="column is-8">
        <div class="field" v-if="staticBlock.ID">
          <label class="label">{{ $resx("Blocks.ID") }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="staticBlock.ID"
              disabled
            />
          </div>
        </div>
        <div class="field">
          <label class="label">{{ $resx("Blocks.Title") }}</label>
          <div class="control">
            <input class="input" type="text" v-model="staticBlock.Title" />
          </div>
        </div>
        <div class="field">
          <label class="label">{{ $resx("Blocks.Subtitle") }}</label>
          <div class="control">
            <markdown
              :value="staticBlock.Subtitle_Md"
              @update="updateSubtitle"
              :autosave="false"
              :wordCount="false"
              :keepactive="true"
              :notInToolbar="[
                'image',
                'heading-1',
                'heading-2',
                'heading-3',
                'heading-bigger',
                'heading-smaller',
                'save',
                'close',
                'toggleMenu',
                'insertCustom',
                'fullWidth'
              ]"
            ></markdown>
          </div>
        </div>
        <div class="field">
          <label class="label">{{ $resx("Blocks.Link") }}</label>
          <div class="control has-icons-right">
            <input
              class="input"
              type="text"
              v-model="staticBlock.CallToAction.Link"
            />
            <page-select
              @input="staticBlock.CallToAction.Link = $event"
            ></page-select>
          </div>
        </div>
        <div class="field">
          <label class="label">{{ $resx("Blocks.Button") }}</label>
          <div class="control">
            <input
              class="input"
              type="text"
              v-model="staticBlock.CallToAction.Text"
            />
          </div>
        </div>
      </form>
      <div class="column is-4">
        <div class="buttons">
          <button
            class="button is-fullwidth is-success"
            @click.prevent="submitBlock('submit')"
            :disabled="submitEnabled ? false : 'disabled'"
          >
            <span>{{ $resx("Buttons.Save") }}</span>
          </button>
          <button
            class="button is-fullwidth"
            @click.prevent="submitBlock('cancel')"
          >
            <span>{{ $resx("Buttons.Cancel") }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { faPlus, faPencil, faTimes } from '@fortawesome/pro-regular-svg-icons'
import loader from '@/components/Loader.vue'
import draggable from 'vuedraggable'
import markdown from '@/components/admin/Markdown.vue'
import pageSelect from '@/components/admin/Pageselect.vue'
import { confirm } from '@/mixins/confirm.js'

export default {
  name: 'modalContentBlocks',
  data () {
    return {
      loading: {
        blocks: true,
        edit: false
      },
      drag: false,
      icons: {
        add: faPlus,
        edit: faPencil,
        delete: faTimes
      },
      edit: false,
      add: false,
      selected: -1,
      staticBlock: false
    }
  },
  mixins: [confirm],
  components: {
    draggable,
    markdown,
    loader,
    pageSelect
  },
  computed: {
    contentBlocks: {
      get () {
        var self = this
        var tmp = []
        if (self.$store.state.contentBlocks[self.$store.state.language]) {
          self.$store.state.contentBlocks[self.$store.state.language].forEach(
            (block) => {
              var found = false
              self.blocks.forEach((pageBlock) => {
                if (!found && block.ID === pageBlock.ID) {
                  found = true
                }
              })
              if (!found) {
                tmp.push(block)
              }
            }
          )
        }
        // self.loading.blocks = false
        return tmp
      },
      set (value) {
        // console.log(value)
      }
    },
    blocks: {
      get () {
        return this.activePage.Cultures[this.$store.state.language]
          .ContentBlocks
      },
      set (value) {
        var self = this
        self.loading.blocks = true
        self.$store.dispatch('setContentBlocksToPage', value)
        var orderArr = []
        value.forEach((block) => {
          orderArr.push(block.ID)
        })
        self
          .$http({
            method: 'POST',
            url: '/contentblocks/' + self.activePage.ID,
            name: process.env.VUE_APP_NAME + '_' + self.$store.state.language,
            api: 'website',
            data: orderArr
          })
          .then(function () {
            self.loading.blocks = false
            self.$store.dispatch('setSnack', {
              message: self.$resx('Blocks.Saved'),
              // color: 'success',
              duration: 2000
            })
          })
          .catch(function (err) {
            self.$store.dispatch('setSnack', {
              message: err.message
            })
            self.loading.blocks = false
          })
      }
    },
    content () {
      return this.activePage.Cultures[this.$store.state.language].Content
    },
    activePage () {
      return this.$store.state.activePage
    },
    dragOptions () {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    },
    selectedBlock () {
      var self = this
      var block = false
      self.$store.state.contentBlocks[self.$store.state.language].forEach(
        (item) => {
          if (item.ID === self.selected) {
            block = item
          }
        }
      )
      return block
    },
    submitEnabled () {
      if (this.add) {
        return (
          JSON.stringify(this.staticBlock) !==
            JSON.stringify({
              ID: false,
              Title: '',
              Subtitle: '',
              Subtitle_Md: '',
              CallToAction: {
                Link: '',
                Text: ''
              }
            }) &&
          this.staticBlock.Subtitle_Md !== '' &&
          this.staticBlock.Title !== ''
        )
      } else {
        return (
          !(
            JSON.stringify(this.staticBlock) ===
            JSON.stringify(this.selectedBlock)
          ) &&
          this.staticBlock.Subtitle_Md !== '' &&
          this.staticBlock.Title !== ''
        )
      }
    }
  },
  methods: {
    selectContent: function (block) {
      var self = this
      if (self.selected === block.ID) {
        self.selected = -1
        self.staticBlock = false
      } else {
        self.selected = block.ID
        self.$store.state.contentBlocks[self.$store.state.language].forEach(
          (item) => {
            if (item.ID === block.ID) {
              self.staticBlock = JSON.parse(JSON.stringify(item))
            }
          }
        )
      }
    },
    submitBlock: function (action) {
      var self = this
      if (action === 'submit') {
        // save block
        self.loading.edit = true
        var method = 'PUT'
        if (self.staticBlock.ID === false) {
          method = 'POST'
          delete self.staticBlock.ID
        }
        var data = JSON.parse(JSON.stringify(self.staticBlock))

        self
          .$http({
            method: method,
            url: '/contentblocks', /// ' + (method === 'PUT' ? self.staticBlock.ID : self.$store.state.language),
            name: process.env.VUE_APP_NAME + '_' + self.$store.state.language,
            api: 'website',
            data: data
          })
          .then(function (response) {
            if (method !== 'PUT') {
              self.staticBlock.ID = response.data.ID
            }
            self.$store.dispatch('getSitemap', true).then(function () {
              self.$store.dispatch('changeBlock', self.staticBlock)
              self.$store.dispatch('getContentBlocks', true).then(() => {
                self.loading.edit = false
                self.$store.dispatch('setSnack', {
                  message: self.$resx('Blocks.SingleSaved')
                })
                self.resetSelected()
              })
            })
          })
          .catch(function (err) {
            self.$store.dispatch('setSnack', {
              message: err.message
            })
            self.loading.edit = false
            self.resetSelected()
          })
      } else {
        self.resetSelected()
      }
    },
    updateSubtitle: function (value) {
      var self = this
      self.staticBlock.Subtitle_Md = value
    },
    resetSelected: function () {
      var self = this
      self.staticBlock = false
      self.selected = -1
      self.add = false
      self.edit = false
      self.$emit('canClose', true)
      self.$store.commit('contentChanged', false)
    },
    addBlock: function () {
      this.tabCulture = ''
      this.staticBlock = {
        ID: false,
        Title: '',
        Subtitle: '',
        Subtitle_Md: '',
        CallToAction: {
          Link: '',
          Text: ''
        }
      }
      this.add = true
      this.$emit('canClose', false)
      this.$store.commit('contentChanged', true)
    },
    deleteBlock: function () {
      var self = this
      var selectedId = self.selectedBlock.ID

      var pages = []
      var thisPage = false
      var message = ''

      if (self.selected > -1) {
        self.$store.state.sitemap.forEach((page) => {
          if (
            page.Cultures.hasOwnProperty(self.$store.state.language) &&
            page.Cultures[self.$store.state.language].hasOwnProperty(
              'ContentBlocks'
            )
          ) {
            page.Cultures[self.$store.state.language].ContentBlocks.forEach(
              (block) => {
                if (block.ID === selectedId) {
                  if (page.ID === self.activePage.ID) {
                    thisPage = true
                  } else {
                    pages.push(page.ID)
                  }
                }
              }
            )
          }
        })

        if (thisPage) {
          if (pages.length > 0) {
            message = self.$resx('Blocks.Remove.ThisAndOther', {
              pages: pages.join('</strong>, <strong>')
            })
            // message = 'De content blok word op deze en op de pagina(\'s) <strong>' + pages.join('</strong>, <strong>') + '</strong> gebruikt. Wil je deze op alle pagina\'s verwijderen? Of alleen op deze pagina?'
          } else {
            message = self.$resx('Blocks.Remove.ThisPage')
            // message = 'Wil je de content blok voorgoed verwijderen? Of alleen op deze pagina?'
          }
        } else {
          if (pages.length > 0) {
            message = self.$resx('Blocks.Remove.Other', {
              pages: pages.join('</strong>, <strong>')
            })
            // message = 'De content blok word op de pagina(\'s) <strong>' + pages.join('</strong>, <strong>') + '</strong> gebruikt. Weet je zeker dat je deze wilt verwijderen?'
          } else {
            message = self.$resx('Blocks.Remove.Overall')
            // message = 'Wil je de content blok voorgoed verwijderen?'
          }
        }

        self.confirm({
          message: message,
          buttons: [
            {
              name: self.$resx('Buttons.Cancel'),
              class: 'is-pulled-left'
            },
            {
              name: self.$resx('Buttons.ThisPage'),
              class:
                'is-pulled-right is-danger' + (!thisPage ? ' is-hidden' : ''),
              action: function () {
                // unlink page - block
                var idx = -1
                for (var i = 0; i < self.blocks.length; i++) {
                  if (self.blocks[i].ID === selectedId) {
                    idx = i
                  }
                }
                if (idx > -1) {
                  var tmp = self.blocks.slice()
                  tmp.splice(idx, 1)
                  self.blocks = tmp
                  self.selected = -1
                }
              }
            },
            {
              name: self.$resx(
                'Buttons.' + (!thisPage ? 'Delete' : 'AllPages')
              ),
              class: 'is-pulled-right is-danger',
              action: function () {
                // completely remove blok
                self.loading.blocks = true
                var idx = -1
                var tmp = ''
                for (
                  var i = 0;
                  i <
                  self.$store.state.contentBlocks[self.$store.state.language]
                    .length;
                  i++
                ) {
                  if (
                    self.$store.state.contentBlocks[self.$store.state.language][i].ID === selectedId
                  ) {
                    idx = i
                  }
                }
                if (idx > -1) {
                  tmp =
                    self.$store.state.contentBlocks[self.$store.state.language].slice()
                  tmp.splice(idx, 1)
                  self.$store.commit('setContentBlocks', tmp)
                }

                tmp = self.$store.state.sitemap
                tmp.forEach((page) => {
                  var changed = -1
                  if (
                    page.Cultures.hasOwnProperty(self.$store.state.language) &&
                    page.Cultures[self.$store.state.language].hasOwnProperty(
                      'ContentBlocks'
                    )
                  ) {
                    for (
                      var i = 0;
                      i <
                      page.Cultures[self.$store.state.language].ContentBlocks
                        .length;
                      i++
                    ) {
                      if (
                        page.Cultures[self.$store.state.language].ContentBlocks[i].ID === selectedId
                      ) {
                        changed = i
                      }
                    }
                    if (changed > -1) {
                      page.Cultures[self.$store.state.language].ContentBlocks.splice(changed, 1)
                      self.$store.dispatch('changePage', page)
                    }
                  }
                })
                // self.loading.blocks = true
                self
                  .$http({
                    method: 'DELETE',
                    url: '/contentblocks/' + selectedId,
                    name:
                      process.env.VUE_APP_NAME +
                      '_' +
                      self.$store.state.language,
                    api: 'website'
                  })
                  .then(function (response) {
                    // self.loading.blocks = true
                    self.$store.dispatch('getSitemap', true).then(function () {
                      // self.$store.dispatch('activatePage', { page: JSON.parse(JSON.stringify(self.activePage)).ID })
                      self.$store
                        .dispatch('getContentBlocks', true)
                        .then(() => {
                          self.loading.blocks = false
                          self.$store.dispatch('setSnack', {
                            message: self.$resx('Blocks.Removed')
                          })
                          self.selected = -1
                        })
                    })
                    // self.$store.dispatch('setSnack', {
                    //   message: self.$resx('Blocks.Removed'),
                    //   color: 'danger'
                    // })
                  })
                  .catch(function (err) {
                    self.$store.dispatch('setSnack', {
                      message: err.message
                    })
                    self.loading.blocks = false
                    self.selected = -1
                  })
                // self.selected = -1
              }
            }
          ]
        })
      }
    }
  },
  mounted () {
    var self = this
    self.loading.blocks = true
    self.$store.dispatch('getContentBlocks').then(() => {
      self.loading.blocks = false
    })
  }
}
</script>

<style lang="scss" scoped>
.edit-fade-enter-active,
.edit-fade-leave-active {
  transition: opacity 0.2s;
}
.edit-fade-enter,
.edit-fade-leave-to {
  opacity: 0;
}
.flip-list-move {
  transition: transform 0.5s;
}
.ghost {
  opacity: 0.3;
}
.blocks-wrapper {
  height: 450px;
  .is-8 {
    // height: 450px;
    // overflow: auto;
    .blocks-header {
      > span {
        display: inline-block;
        vertical-align: top;
        width: 50%;
        padding: 0px 10px;
        text-align: left;
        font-size: 75%;
        opacity: 0.8;
        height: 26px;
      }
    }

    .blocks-list {
      height: calc(424px - 1.5rem);
      overflow: auto;
      width: 50%;
      display: inline-block;
      vertical-align: top;
      padding: 0px 10px;

      ul {
        min-height: 132px;
        padding-bottom: 66px;

        li {
          padding: 5px 10px;
          height: auto;
          display: block;
          width: 100%;
          border: 1px solid $grey-lighter;
          background-color: $grey-bg;
          border-radius: 4px;
          cursor: pointer;
          @include text-overflow;

          + li {
            margin-top: 10px;
          }

          &.selected {
            border-color: $grey;
          }

          small {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
      &.onpage {
        li {
          background-color: $grey-lighter;
        }
      }
    }
  }
}
.form-wrapper {
  > .is-8 {
    height: 450px;
    overflow: auto;

    // .tabs{
    //   a.select{
    //     height: auto;
    //     select{
    //       border: none;
    //       padding-top: 0;
    //       padding-bottom: 0;
    //       padding-right: 0;
    //       padding-left: 0;
    //       height: 26px;
    //     }
    //   }

    // }
    /deep/ .vue-simplemde {
      .CodeMirror {
        min-height: 100px;
        height: 100px;
      }
      .CodeMirror-scroll {
        min-height: 1px;
      }
    }
  }
}
</style>
